import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { SettingsCreateBidItemFieldRequest, SettingsUpdateBidItemFieldRequest } from '../../../../../../../core/projects.actions';

@Component({
  standalone: false,
  selector: 'app-fields-item',
  templateUrl: './fields-item.component.html',
  styleUrls: ['./fields-item.component.scss'],
})
export class FieldsItemComponent implements OnInit, OnDestroy, OnChanges {
  @Input() formGroup: FormGroup;
  @Input() groupId: string;
  @Input() bidItemId: string;
  @Input() canEdit = true;

  typeOptions: { value: string; label: string }[] = [
    { value: 'number', label: 'Number' },
    { value: 'string', label: 'String' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'json', label: 'JSON' },
  ];

  errorMessages: Record<string, string> = {};
  errorMessageMap: Record<string, Record<string, string>> = {
    label: { required: 'This field is required.' },
    name: { required: 'This field is required.' },
    type: { required: 'This field is required.' },
  };

  private nameManuallyEdited = false;
  private labelDrivenUpdate = false;
  private readonly onDestroy = new Subject<void>();

  constructor(
    private commonService: CommonService,
    private projectsService: ProjectsService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.projectsService.checkForErrors
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.checkForErrors());
    this.applyCanEdit();
    this.setupLabelToNameSync();
  }

  private setupLabelToNameSync(): void {
    if (!this.formGroup) return;
    // Existing fields already have a name — don't auto-populate
    if (this.formGroup.get('name')?.value) {
      this.nameManuallyEdited = true;
    }

    this.formGroup.get('name')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        if (!this.labelDrivenUpdate) {
          this.nameManuallyEdited = true;
        }
      });

    this.formGroup.get('label')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((label: string) => {
        if (this.nameManuallyEdited) return;
        const name = (label || '').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        this.labelDrivenUpdate = true;
        this.formGroup.get('name')?.setValue(name);
        this.labelDrivenUpdate = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['canEdit'] || changes['formGroup']) {
      this.applyCanEdit();
    }
  }

  private applyCanEdit(): void {
    if (!this.formGroup) return;
    if (this.canEdit) {
      this.formGroup.enable();
    } else {
      this.formGroup.disable();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  onIsOptionalToggle(event: MatSlideToggleChange): void {
    this.formGroup?.patchValue({ isOptional: event.checked });
  }

  onRequiredToggle(event: MatSlideToggleChange): void {
    this.formGroup?.patchValue({ required: event.checked });
  }

  checkForErrors(currentField?: string): void {
    if (!this.formGroup) return;
    const validationErrors = this.commonService.checkFormValidation(
      this.formGroup,
      this.errorMessageMap,
      currentField
    );
    if (currentField !== undefined) {
      const next = { ...this.errorMessages };
      if (validationErrors && validationErrors[currentField]) {
        next[currentField] = validationErrors[currentField];
      } else {
        delete next[currentField];
      }
      this.errorMessages = next;
    } else {
      this.errorMessages = validationErrors ? { ...validationErrors } : {};
    }
  }

  onSubmit(): void {
    if (!this.formGroup) return;
    if (!this.bidItemId) return;
    if (this.formGroup.invalid) {
      this.checkForErrors();
      this.projectsService.checkForErrors.next(true);
      return;
    }
    const raw = this.formGroup.getRawValue();
    const body = {
      id: this.groupId.startsWith('temp_') ? null : this.groupId,
      display_order: 0,
      name: raw?.name ?? '',
      label: raw?.label ?? '',
      type: raw?.type ?? '',
      uom: raw?.uom || null,
      estimate_station_property: null,
      validation_rules: {
        required: !!raw?.required,
        min: raw?.required_min ?? null,
        max: raw?.required_max ?? null,
      },
      recent_field_id: null,
      recent_field_label: null,
      is_optional: !!raw?.isOptional,
      range_values: {
        min: raw?.range_min ?? null,
        max: raw?.range_max ?? null,
      },
    };
    if (this.groupId.startsWith('temp_')) {
      this.store.dispatch(SettingsCreateBidItemFieldRequest({ bidItemId: this.bidItemId, body }));
    } else {
      this.store.dispatch(SettingsUpdateBidItemFieldRequest({ fieldId: this.groupId, bidItemId: this.bidItemId, body }));
    }
  }
}
